/**
 * The menu: every screen the platform serves, the default suites they sit in,
 * and a person's own arrangement of them.
 *
 * A layout is what somebody saved — suites with their names and order, the
 * screens in each and their order, renamed screens and hidden ones. It is
 * never trusted as the list of screens. `normalizeLayout` reconciles it with
 * the screens this build actually has, every time it is read:
 *
 *  - a screen the layout names that no longer exists is dropped;
 *  - a screen this build added since the layout was saved is not lost — it
 *    goes to its default suite if the person still has it, else to the first;
 *  - a screen named twice keeps its first place.
 *
 * So a release that adds a screen cannot hide it from somebody who customised
 * their menu last month, and a stale layout cannot put a dead link on it.
 */

export interface ScreenDef {
  to: string;
  /** The default label; a layout can rename it. */
  label: string;
  /** SVG path data for the 24×24 icon. */
  icon: string;
}

export interface NavGroup {
  /** Stable. The default suites keep their ids when renamed, which is how a renamed suite keeps its description. */
  id: string;
  name: string;
  screens: string[];
}

export interface NavLayout {
  version: 1;
  groups: NavGroup[];
  /** Routes left out of the menu. Still reachable by link; just not listed. */
  hidden: string[];
  /** Route → the person's own label for it. */
  labels: Record<string, string>;
}

import { DASHBOARD_SCREENS } from "../demo/boards/dashboardScreens";

export const PREFERENCE_KEY = "nav.layout";

export const SCREENS: readonly ScreenDef[] = [
  { to: "/", label: "Dashboard", icon: "M3 3h7v7H3zM14 3h7v4h-7zM14 10h7v11h-7zM3 13h7v8H3z" },
  { to: "/overview", label: "Dynamic dashboard", icon: "M3 12a9 9 0 0118 0M12 12l4-3M12 21v-2" },
  { to: "/drawing", label: "Interactive Map", icon: "M3 20 21 4M7 4l4 4M13 16l4 4" },
  { to: "/section-3d", label: "Section in 3D", icon: "M12 3l8 4.5v9L12 21l-8-4.5v-9zM12 12l8-4.5M12 12v9M12 12L4 7.5" },
  { to: "/friction", label: "Friction testing", icon: "M3 17h18M5 17V9M9 17V6M13 17v-7M17 17V8" },
  { to: "/rubber", label: "Rubber removal", icon: "M4 18c4-9 12-9 16 0M8 18v2M16 18v2" },
  { to: "/markings", label: "Marking & visual aids", icon: "M4 20 8 4M16 4l4 16M12 4v3M12 10v4M12 17v3" },
  {
    to: "/agl",
    label: "AGL & nav-aids",
    icon: "M12 2v3M4.9 4.9l2.1 2.1M2 12h3M19 12h3M17 7l2.1-2.1M8 16a4 4 0 118 0v2H8zM10 21h4",
  },
  { to: "/electrical", label: "Electrical & utilities", icon: "M13 2 4 14h7l-1 8 9-12h-7z" },
  /* the building's electricity, water, gas and chilled-water meters and
     whether each is reporting: a dial with its needle */
  { to: "/meters", label: "Meter register", icon: "M4 17a8 8 0 1116 0M12 17l4-5M3 21h18" },
  { to: "/buildings", label: "Buildings", icon: "M4 21V7l8-4 8 4v14M9 21v-6h6v6M8 11h.01M12 11h.01M16 11h.01" },
  { to: "/plans", label: "Building plans", icon: "M4 4h16v16H4zM4 10h16M10 10v10" },
  { to: "/landside", label: "Landside", icon: "M3 17h18M6 17v-4h12v4M9 13V9h6v4M12 5v4" },
  { to: "/engineering", label: "Engineering", icon: "M14 6a4 4 0 10-4 4l7 7 3-3-7-7a4 4 0 001-1z" },
  { to: "/projects", label: "Project Screen", icon: "M4 6h16M4 12h10M4 18h7M18 14l3 3-3 3" },
  /* The platform's capital register. The demo's own Projects board keeps
     "/projects"; this one is the record, and it sits above the board in
     Engineering because it is what an engineer opens to enter anything. */
  { to: "/capital-projects", label: "Capital projects", icon: "M3 21h18M6 21V8l6-4 6 4v13M10 21v-5h4v5" },
  {
    to: "/testing-records",
    label: "Testing records",
    icon: "M9 3h6M10 3v6L5 19a1.5 1.5 0 001.3 2h11.4A1.5 1.5 0 0019 19l-5-10V3M7.5 14h9",
  },
  { to: "/work-orders", label: "Work orders", icon: "M9 3h6v3H9zM5 6h14v15H5zM8 11h8M8 15h5" },
  { to: "/inspection-platform", label: "Inspection platform", icon: "M5 4h14v16H5zM8 9l2 2 4-4M8 15h8" },
  { to: "/assessments", label: "Assessments", icon: "M12 3l2.5 5 5.5.8-4 3.9 1 5.5-5-2.6-5 2.6 1-5.5-4-3.9 5.5-.8z" },
  { to: "/alerts", label: "Alerts", icon: "M12 3 2 20h20zM12 10v4M12 17v1" },
  { to: "/compliance", label: "Compliance", icon: "M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6z" },
  { to: "/library", label: "Library", icon: "M4 4h6v16H4zM10 4h6v16h-6zM16 6l4-1 3 15-4 1z" },
  /* Deep thinking: one front door and three rooms; the nine screens are tabs inside them */
  { to: "/think", label: "Ask", icon: "M12 3a7 7 0 00-4 12.7V18h8v-2.3A7 7 0 0012 3zM9.5 21h5M10 9.5a2 2 0 114 0c0 1.5-2 1.7-2 3.3" },
  {
    to: "/decide",
    label: "Decide the program",
    icon: "M9 11l3 3 8-8M20 12v7a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h9",
  },
  { to: "/window", label: "Find a window", icon: "M8 2v4M16 2v4M3 10h18M5 4h14v16H5zM9 15l2 2 4-4" },
  { to: "/trust", label: "Can we trust it", icon: "M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6zM9 12l2 2 4-4" },
  { to: "/airfield", label: "Dynamic map", icon: "M12 21s7-6.4 7-11a7 7 0 10-14 0c0 4.6 7 11 7 11zM12 10h.01" },
  { to: "/inspections", label: "Inspection records", icon: "M6 3h12v18H6zM9 8h6M9 12h6M9 16h3" },
  { to: "/instruments", label: "Instruments", icon: "M3 12a9 9 0 0118 0M12 12l5-4" },
  { to: "/aodb", label: "Flight record", icon: "M4 4h16v16H4zM4 9h16M9 9v11M13 13h4M13 16h4" },
  { to: "/allocation", label: "Resource allocation", icon: "M3 5h8M3 10h13M3 15h6M3 20h11M14 5h7M18 15h3" },
  { to: "/passenger-flow", label: "Passenger flow", icon: "M7 4a2 2 0 110 4 2 2 0 010-4zM5 21v-6l-1-4h6l-1 4v6M14 12h7M17 9l3 3-3 3" },
  {
    to: "/flight-feeds",
    label: "Flight data feeds",
    /* a message arriving down a line into a record */
    icon: "M3 6h8M3 10h5M3 14h8M13 12h3M14 9l3 3-3 3M18 5h3v14h-3",
  },
  {
    to: "/contacts",
    label: "Contact directory",
    icon: "M4 4h14v16H4zM8 9a2 2 0 104 0 2 2 0 00-4 0M6.5 15c.8-1.6 2-2.5 3.5-2.5s2.7.9 3.5 2.5M20 7v2M20 12v2",
  },
  { to: "/notify-rules", label: "Who is told", icon: "M4 6h16v10H8l-4 4zM8 10h8M8 13h5" },
  {
    to: "/calls",
    label: "Staff calls",
    /* a handset */
    icon: "M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1 1 .4 1.9.7 2.8a2 2 0 01-.5 2.1L8 9.9a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.8.7a2 2 0 011.7 2z",
  },
  {
    to: "/call-policy",
    label: "Who can call whom",
    /* a shield with an arrow each way */
    icon: "M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6zM8 10h8M14 8l2 2-2 2M16 14H8M10 12l-2 2 2 2",
  },
  { to: "/vdgs", label: "Visual docking", icon: "M4 4h16v6H4zM8 7h8M12 10v10M8 20h8M12 14l-3 3M12 14l3 3" },
  {
    to: "/intelligence-report",
    label: "Intelligence report",
    icon: "M12 3v3M12 18v3M3 12h3M18 12h3M12 8a4 4 0 100 8 4 4 0 000-8zM5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2",
  },
  {
    to: "/d-brief",
    label: "D-Brief",
    icon: "M5 4h14v16H5zM8 9h8M8 13h8M8 17h5M17 2v4M7 2v4",
  },
  {
    to: "/decision-assist",
    label: "Decision Room",
    icon: "M4 6h16M4 12h10M4 18h7M17 10l3 3-3 3M14 13h6",
  },
  { to: "/crisis", label: "Crisis Desk", icon: "M12 3l9 16H3zM12 10v4M12 17h.01" },
  {
    to: "/urgent-inspection",
    label: "Urgent Inspection",
    /* a cloud with a lightning bolt over a runway line */
    icon: "M17 14a4 4 0 000-8 6 6 0 00-11.3 2A3.5 3.5 0 006 14M12 10l-2 4h4l-2 4M3 21h18",
  },
  { to: "/cash", label: "Cash & Bottom Line", icon: "M3 7h18v10H3zM12 9a3 3 0 100 6 3 3 0 000-6zM6 12h.01M18 12h.01" },
  {
    to: "/capacity-expansion",
    label: "Capacity & Expansion",
    icon: "M4 20V10M10 20V4M16 20v-8M22 20H2M20 4l-4 4M20 4h-4M20 4v4",
  },
  { to: "/capex", label: "Capital Projects", icon: "M3 21h18M6 21V9h4v12M14 21V3h4v18M10 21v-6h4v6" },
  { to: "/regulatory", label: "Regulatory Demands", icon: "M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6zM9 12l2 2 4-4" },
  { to: "/benchmark", label: "Benchmark", icon: "M3 3v18h18M7 14l4-4 4 3 5-6M18 7h2v2" },
  { to: "/improvement", label: "Business Improvement", icon: "M3 17l6-6 4 4 8-8M15 7h6v6" },
  {
    to: "/green-program",
    label: "Green & LEED",
    icon: "M12 22c-4-4-8-7-8-12a8 8 0 0116 0c0 5-4 8-8 12zM12 6v8M9 10l3 3 3-3",
  },
  {
    to: "/administration",
    label: "Administration",
    icon: "M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 7a4 4 0 108 0 4 4 0 00-8 0M22 21v-2a4 4 0 00-3-3.87",
  },
  {
    to: "/migration",
    label: "Replacing a system",
    icon: "M4 7h11l-3-3M20 17H9l3 3",
  },
  {
    to: "/finance-system",
    label: "The finance system",
    /* a ledger page with a line of figures, and the arrow that brings them in */
    icon: "M6 3h9l4 4v14H6zM9 12h7M9 16h5M9 8h3",
  },
  {
    to: "/equipment",
    label: "Equipment",
    /* a flatbed and two wheels; the register is vehicles and plant */
    icon: "M3 7h10v8H3zM13 10h4l3 3v2h-7zM7 18a2 2 0 100-4 2 2 0 000 4zM17 18a2 2 0 100-4 2 2 0 000 4z",
  },
  {
    to: "/asset-register",
    label: "Asset register",
    /* a tree: one node branching to three */
    icon: "M12 3v5M12 8H6v4M12 8h6v4M4 12h4v4H4zM10 12h4v4h-4zM16 12h4v4h-4zM6 16v5M18 16v5",
  },
  {
    to: "/asset-condition",
    label: "Condition and risk",
    /* a five-by-five grid with one cell marked */
    icon: "M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18M16 4h4v4h-4z",
  },
  {
    to: "/asset-maintenance",
    label: "Maintenance plans",
    /* a calendar with a spanner mark */
    icon: "M4 5h16v15H4zM4 9h16M8 3v4M16 3v4M9 13l2 2 4-4",
  },
  {
    to: "/asset-lifecycle",
    label: "Lifecycle and capital plan",
    /* a curve running down to a renewal step */
    icon: "M3 5c5 0 8 3 10 9h4v-4l4 4-4 4v-4M3 20h18",
  },
  /* Human Resources: the people, their files, their cost under Saudi law */
  {
    to: "/hr",
    label: "HR home",
    /* a house with a person in the door */
    icon: "M3 11l9-7 9 7M5 10v10h14V10M12 13a2 2 0 100-4 2 2 0 000 4zM9 20v-2a3 3 0 016 0v2",
  },
  {
    to: "/hr-me",
    label: "My check-in",
    /* a phone with a pin */
    icon: "M7 2h10v20H7zM12 18h.01M12 6a3 3 0 00-3 3c0 2 3 5 3 5s3-3 3-5a3 3 0 00-3-3z",
  },
  {
    to: "/hr-notifications",
    label: "Messages",
    /* an envelope and a speech bubble */
    icon: "M3 6h12v9H3zM3 6l6 5 6-5M15 10h6v7h-3l-3 3v-3",
  },
  {
    to: "/hr-attendance",
    label: "Attendance",
    /* a calendar with a tick */
    icon: "M4 5h16v16H4zM4 9h16M8 3v4M16 3v4M9 15l2 2 4-4",
  },
  {
    to: "/hr-records",
    label: "Records registry",
    /* a filing drawer */
    icon: "M4 4h16v6H4zM4 10h16v10H4zM10 7h4M10 14h4",
  },
  {
    to: "/hr-calculators",
    label: "Calculators",
    /* a calculator */
    icon: "M6 3h12v18H6zM8 6h8v3H8zM8 12h2M12 12h2M16 12h0M8 16h2M12 16h2M16 16v2",
  },
  {
    to: "/hr-reports",
    label: "HR reports",
    /* a page with bars */
    icon: "M5 3h14v18H5zM8 17v-4M12 17V9M16 17v-6",
  },
  {
    to: "/hr-access",
    label: "Access levels",
    /* a padlock */
    icon: "M6 11h12v10H6zM8 11V7a4 4 0 018 0v4M12 15v3",
  },
  {
    to: "/hr-people",
    label: "Workforce",
    /* two people, one behind the other */
    icon: "M9 11a4 4 0 100-8 4 4 0 000 8zM2 21v-1a6 6 0 0112 0v1M16 3.5a4 4 0 010 7M22 21v-1a6 6 0 00-4-5.6",
  },
  {
    to: "/hr-cost",
    label: "Manpower cost",
    /* a stack of coins */
    icon: "M4 7c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3zM4 7v5c0 1.7 3.6 3 8 3s8-1.3 8-3V7M4 12v5c0 1.7 3.6 3 8 3s8-1.3 8-3v-5",
  },
  {
    to: "/hr-labour",
    label: "Labour cost of work",
    /* a clock: hours, priced */
    icon: "M12 7v5l3 2M21 12a9 9 0 11-9-9M17 3h4v4",
  },
  {
    to: "/hr-saudization",
    label: "Saudization",
    /* a share of a whole */
    icon: "M12 3v9h9M21 12a9 9 0 11-9-9",
  },
  {
    to: "/hr-rules",
    label: "Saudi labour rules",
    /* a page of articles */
    icon: "M6 3h9l3 3v15H6zM9 9h6M9 13h6M9 17h4",
  },
  {
    to: "/flights",
    label: "Today's flights",
    /* a wing and a track: the movement itself */
    icon: "M2 16l20-6-7 10-3-3-4 4zM10 13L7 8",
  },
  {
    to: "/flight-delays",
    label: "Delays",
    /* a clock with a warning mark */
    icon: "M12 7v5l3 2M21 12a9 9 0 11-9-9M19 3v4M19 9v.01",
  },
  {
    to: "/stands",
    label: "Stands and gates",
    /* an aircraft nose against a bridge */
    icon: "M4 20h16M7 20V9l5-4 5 4v11M10 20v-5h4v5M3 13h4M17 13h4",
  },
  {
    to: "/turnarounds",
    label: "Turnarounds",
    /* chocks on, chocks off: the ground time between them */
    icon: "M5 12h14M7 8v8M17 8v8M9 12l3-3M9 12l3 3",
  },
  {
    to: "/fids",
    label: "Flight information",
    /* a board on a post, which is what a passenger looks up at */
    icon: "M4 4h16v10H4zM12 14v6M8 20h8M7 7h6M7 10h4",
  },
  {
    to: "/facility-inspections",
    label: "Facility inspections",
    /* a clipboard with a tick: each trade's inspection of a facility */
    icon: "M9 4h6v3H9zM7 5H5v16h14V5h-2M8.5 14l2.5 2.5 5-5",
  },
  {
    to: "/settings",
    label: "Settings",
    icon: "M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 01-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 01-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 01-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 010-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 012.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 014 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 012.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 010 4h-.1a1.7 1.7 0 00-1.5 1z",
  },
  /* the systems dashboard's boards, each as a screen of its own */
  ...DASHBOARD_SCREENS.map(({ to, label, icon }) => ({ to, label, icon })),
];

export const SCREEN_BY_ROUTE: ReadonlyMap<string, ScreenDef> = new Map(SCREENS.map((s) => [s.to, s]));

/*
 * The default suites. The grouping follows the demo's own dashboard: the
 * airfield, the estate, engineering, operations, safety, certification, the
 * records, the business, the platform, then the decisions made from them.
 * The dashboard's boards are screens in their own right, so a person can see
 * from the menu that SMS or the certification registers exist.
 */
export const DEFAULT_GROUPS: readonly NavGroup[] = [
  { id: "overview", name: "Overview", screens: ["/", "/overview"] },
  {
    id: "executive",
    name: "Executive",
    screens: [
      "/executive-kpis",
      "/decision-assist",
      "/crisis",
      "/capacity-expansion",
      "/regulatory",
      "/benchmark",
      "/improvement",
      "/green-program",
      "/approvals",
      "/sustainability",
    ],
  },
  /*
   * The money, and where it comes from, in one place directly under
   * Executive. The three desks read the same `Finance` figures and the
   * fourth screen is the system those figures are read from — kept together
   * because the first question anybody asks of a number on the cash desk is
   * where it came from, and the answer used to be two suites away.
   *
   * The desks come first because they are what gets opened daily; the
   * connector is last because it is where you go when a figure is doubted.
   */
  {
    id: "finance-system",
    name: "Financial System",
    screens: ["/cash", "/financials", "/capex", "/finance-system"],
  },
  {
    id: "airfield",
    name: "Aerodrome",
    screens: [
      "/drawing",
      "/section-3d",
      "/friction",
      "/rubber",
      "/markings",
      "/agl",
      "/vdgs",
      "/electrical",
      "/meters",
      "/airfield-inspections",
      "/urgent-inspection",
      "/instruments",
    ],
  },
  {
    /*
     * The flights that use the airfield, between the airfield itself and the
     * operation around them. Its own suite rather than four more screens in
     * Operations control, which already carries fourteen: a layer buried in
     * another suite reads as an accessory to it.
     */
    id: "flightops",
    name: "Flight operations",
    screens: ["/flights", "/aodb", "/flight-feeds", "/allocation", "/passenger-flow", "/flight-delays", "/stands", "/turnarounds", "/fids"],
  },
  {
    id: "estate",
    // Renamed from "The estate", then "Airport Assets"; merged with Asset
    // Management on 2026-09-26. The id is deliberately unchanged: a person
    // who renamed or rearranged this suite keeps their arrangement, and the
    // four Asset Management screens join it by default rather than arriving
    // as a second suite about the same things.
    name: "Asset Management",
    screens: [
      /* what there is */
      "/asset-register",
      "/facilities",
      "/buildings",
      "/plans",
      "/landside",
      "/equipment",
      /* what state it is in */
      "/asset-condition",
      "/systems",
      "/facility-inspections",
      /* what to do about it, and what it costs */
      "/asset-maintenance",
      "/asset-lifecycle",
      /* its records */
      "/documents",
    ],
  },
  {
    /*
     * The people who do the work and what they cost, directly under Asset
     * Management because that is where their hours are spent: a work order's
     * labour and a maintenance plan's year are priced from here (2026-09-26).
     */
    id: "hr",
    // Named "HR" on 2026-09-26, then "Human Resources" the same day.
    name: "Human Resources",
    screens: [
      "/hr",
      "/hr-me",
      "/hr-people",
      "/hr-attendance",
      "/hr-records",
      "/hr-notifications",
      "/hr-calculators",
      "/hr-reports",
      "/hr-cost",
      "/hr-labour",
      "/hr-saudization",
      "/hr-access",
      "/hr-rules",
    ],
  },
  {
    id: "engineering",
    name: "Engineering",
    screens: [
      "/engineering",
      "/visual-analytics",
      "/maintenance",
      "/maintenance-reporting",
      "/capital-projects",
      "/projects",
      "/testing-records",
    ],
  },
  {
    id: "ops",
    name: "Operations control",
    screens: [
      "/operations",
      "/passengers",
      "/stakeholders",
      "/work-orders",
      "/inspection-platform",
      "/assessments",
      "/alerts",
      "/intelligence-report",
      "/d-brief",
      "/library",
      "/logistics",
      "/airfield",
      "/inspections",
    ],
  },
  {
    id: "safety",
    name: "Safety & compliance",
    screens: ["/safety", "/sms", "/compliance", "/compliance-framework", "/standards"],
  },
  {
    id: "cert",
    name: "Certification",
    screens: ["/certification", "/certification/assets", "/certification/renewals", "/certification/documents"],
  },
  {
    id: "platform",
    name: "Platform",
    screens: [
      "/intelligence",
      "/diagnostics",
      "/search",
      "/ask",
      "/administration",
      "/contacts",
      "/notify-rules",
      "/calls",
      "/call-policy",
      "/migration",
      "/settings",
    ],
  },
  {
    id: "operations",
    name: "Deep thinking",
    screens: ["/think", "/decide", "/window", "/trust"],
  },
];

const DEFAULT_GROUP_OF: ReadonlyMap<string, string> = new Map(
  DEFAULT_GROUPS.flatMap((g) => g.screens.map((to) => [to, g.id] as const)),
);

export function defaultLayout(): NavLayout {
  return { version: 1, groups: DEFAULT_GROUPS.map((g) => ({ ...g, screens: [...g.screens] })), hidden: [], labels: {} };
}

export const MAX_NAME = 40;

const asString = (v: unknown): string | null => (typeof v === "string" ? v : null);

/** Reconcile anything read back — a saved layout, possibly old or damaged — with the screens that exist. */
/** Names a default suite has had before, by id. */
const FORMER_NAMES: Record<string, readonly string[]> = {
  estate: ["The estate", "Airport Assets"],
  hr: ["HR"],
};

export function normalizeLayout(raw: unknown): NavLayout {
  const src = raw && typeof raw === "object" ? (raw as Partial<Record<keyof NavLayout, unknown>>) : null;
  if (!src || !Array.isArray(src.groups)) return defaultLayout();

  const placed = new Set<string>();
  const ids = new Set<string>();
  const groups: NavGroup[] = [];
  for (const g of src.groups as unknown[]) {
    if (!g || typeof g !== "object") continue;
    const rec = g as Record<string, unknown>;
    let id = asString(rec.id)?.trim() || `suite-${groups.length + 1}`;
    while (ids.has(id)) id = `${id}-x`;
    ids.add(id);
    const name = (asString(rec.name) ?? "").trim().slice(0, MAX_NAME) || "Untitled suite";
    const screens: string[] = [];
    for (const to of (Array.isArray(rec.screens) ? rec.screens : []).map(asString)) {
      // `placed` is updated as each is taken, so a screen named twice in one suite keeps its first place too.
      if (!to || !SCREEN_BY_ROUTE.has(to) || placed.has(to)) continue;
      placed.add(to);
      screens.push(to);
    }
    groups.push({ id, name, screens });
  }
  if (groups.length === 0) return defaultLayout();

  /*
   * A whole suite this build added since the layout was saved arrives as a
   * suite, in its default place — not scattered into the first one. It is
   * new when none of its screens appears anywhere in the layout; a suite the
   * person took apart still has its screens placed elsewhere, and stays gone.
   * (Asset Management, 2026-09-26, landed in Overview for everyone with a
   * saved menu before this.)
   */
  DEFAULT_GROUPS.forEach((dg, i) => {
    if (ids.has(dg.id) || dg.screens.some((to) => placed.has(to))) return;
    const before = DEFAULT_GROUPS.slice(0, i)
      .map((g) => groups.findIndex((x) => x.id === g.id))
      .filter((at) => at >= 0);
    const at = before.length ? Math.max(...before) + 1 : 0;
    groups.splice(at, 0, { id: dg.id, name: dg.name, screens: [] });
    ids.add(dg.id);
  });

  /*
   * A suite still carrying a name the product used to give it was never
   * renamed by the person, so it takes today's name. A name they chose stays.
   */
  for (const g of groups) {
    const current = DEFAULT_GROUPS.find((d) => d.id === g.id);
    if (current && FORMER_NAMES[g.id]?.includes(g.name)) g.name = current.name;
  }

  // Screens this build has that the layout does not place: to their default suite if it survives, else the first.
  for (const screen of SCREENS) {
    if (placed.has(screen.to)) continue;
    const home = groups.find((g) => g.id === DEFAULT_GROUP_OF.get(screen.to)) ?? groups[0];
    home.screens.push(screen.to);
    placed.add(screen.to);
  }

  const hidden = (Array.isArray(src.hidden) ? src.hidden : [])
    .map(asString)
    .filter((to): to is string => !!to && SCREEN_BY_ROUTE.has(to));
  const labels: Record<string, string> = {};
  if (src.labels && typeof src.labels === "object") {
    for (const [to, v] of Object.entries(src.labels as Record<string, unknown>)) {
      const label = asString(v)?.trim().slice(0, MAX_NAME);
      if (label && SCREEN_BY_ROUTE.has(to) && label !== SCREEN_BY_ROUTE.get(to)!.label) labels[to] = label;
    }
  }
  return { version: 1, groups, hidden: [...new Set(hidden)], labels };
}

export function labelOf(layout: NavLayout, to: string): string {
  return layout.labels[to] ?? SCREEN_BY_ROUTE.get(to)?.label ?? to;
}

export function isDefaultLayout(layout: NavLayout): boolean {
  return JSON.stringify(normalizeLayout(layout)) === JSON.stringify(defaultLayout());
}

/** The group a screen sits in under this layout. */
export function groupOf(layout: NavLayout, to: string): NavGroup | undefined {
  return layout.groups.find((g) => g.screens.includes(to));
}

/* ---- edits, pure, for the customise dialog ------------------------------------ */

export function moveGroup(layout: NavLayout, id: string, by: -1 | 1): NavLayout {
  const i = layout.groups.findIndex((g) => g.id === id);
  const j = i + by;
  if (i < 0 || j < 0 || j >= layout.groups.length) return layout;
  const groups = [...layout.groups];
  [groups[i], groups[j]] = [groups[j], groups[i]];
  return { ...layout, groups };
}

/** Put a screen into a group at an index (end when omitted), taking it out of wherever it was. */
export function placeScreen(layout: NavLayout, to: string, groupId: string, index?: number): NavLayout {
  const groups = layout.groups.map((g) => ({ ...g, screens: g.screens.filter((s) => s !== to) }));
  const target = groups.find((g) => g.id === groupId);
  if (!target) return layout;
  const at = index == null ? target.screens.length : Math.max(0, Math.min(index, target.screens.length));
  target.screens.splice(at, 0, to);
  return { ...layout, groups };
}

export function moveScreen(layout: NavLayout, to: string, by: -1 | 1): NavLayout {
  const g = groupOf(layout, to);
  if (!g) return layout;
  const i = g.screens.indexOf(to);
  const j = i + by;
  if (j < 0 || j >= g.screens.length) {
    // Past the end of its suite: into the neighbouring suite, at the near end.
    const gi = layout.groups.findIndex((x) => x.id === g.id);
    const next = layout.groups[gi + by];
    return next ? placeScreen(layout, to, next.id, by === 1 ? 0 : undefined) : layout;
  }
  return placeScreen(layout, to, g.id, j);
}

export function renameGroup(layout: NavLayout, id: string, name: string): NavLayout {
  return { ...layout, groups: layout.groups.map((g) => (g.id === id ? { ...g, name: name.slice(0, MAX_NAME) } : g)) };
}

/**
 * Rename a screen. The default name, or nothing, clears the rename — except
 * that a blank is kept while somebody is typing (pass `{ typing: true }`), so
 * clearing the field does not snap the old name back under their cursor.
 * `normalizeLayout` drops a blank on save.
 */
export function renameScreen(layout: NavLayout, to: string, label: string, opts: { typing?: boolean } = {}): NavLayout {
  const labels = { ...layout.labels };
  const next = label.slice(0, MAX_NAME);
  if (next.trim() === SCREEN_BY_ROUTE.get(to)?.label || (!next.trim() && !opts.typing)) delete labels[to];
  else labels[to] = next;
  return { ...layout, labels };
}

export function addGroup(layout: NavLayout, name = "New suite"): { layout: NavLayout; id: string } {
  let n = layout.groups.length + 1;
  const taken = new Set(layout.groups.map((g) => g.id));
  while (taken.has(`suite-${n}`)) n += 1;
  const id = `suite-${n}`;
  return { layout: { ...layout, groups: [...layout.groups, { id, name, screens: [] }] }, id };
}

/** Remove a suite. Its screens are never lost: they go to the suite above it, or the one below if it was first. */
export function removeGroup(layout: NavLayout, id: string): NavLayout {
  const i = layout.groups.findIndex((g) => g.id === id);
  if (i < 0 || layout.groups.length === 1) return layout;
  const orphans = layout.groups[i].screens;
  const groups = layout.groups.filter((g) => g.id !== id).map((g) => ({ ...g, screens: [...g.screens] }));
  groups[Math.max(0, i - 1)].screens.push(...orphans);
  return { ...layout, groups };
}

export function toggleHidden(layout: NavLayout, to: string): NavLayout {
  const hidden = layout.hidden.includes(to) ? layout.hidden.filter((h) => h !== to) : [...layout.hidden, to];
  return { ...layout, hidden };
}
